import { formatCitedContent, isCitationReference, parseCitationIndex } from "#features/citations/citedContent.ts";
import type { Conversation, ConversationMessage, ConversationSource } from "#types/types.ts";
import { useCallback, useMemo, useRef } from "react";

// One address: no spaces, no second "@", no "," a list of recipients would have.
const EMAIL_ADDRESS = /^[^\s@,]+@[^\s@,]+\.[^\s@,]+$/;

// The one address a mailto: link writes to, read as a URL: a query (?subject=…), an apostrophe or an accented letter
// still name a person; anything else, a list of recipients included, is no address.
const mailtoAddress = (href: string): string | undefined => {
    if (!href.toLowerCase().startsWith("mailto:")) {
        return undefined;
    }
    try {
        const address = decodeURIComponent(new URL(href).pathname);

        return EMAIL_ADDRESS.test(address) ? address : undefined;
    } catch {
        // A malformed URL or escape: an ordinary link.
        return undefined;
    }
};

// Every source in the conversation, the same list while none changes: what's built on it (the markdown's link component)
// then stays one component across a streamed reply's chunks, rather than remounting every link, open cards included.
const useConversationSources = (messages: ConversationMessage[]) => {
    const sourcesRef = useRef<ConversationSource[]>([]);

    return useMemo(() => {
        const sources = messages.flatMap(message => message.sources);
        const previous = sourcesRef.current;
        if (sources.length !== previous.length || sources.some((source, index) => source !== previous[index])) {
            sourcesRef.current = sources;
        }

        return sourcesRef.current;
    }, [messages]);
};

// What an answer reads of its conversation: its id and every source its markers may cite.
export interface CitedConversation {
    conversationId: string;
    sources: readonly ConversationSource[];
}

// The same object while the conversation's id and sources stay the same, which a streamed chunk doesn't change: an answer
// that reads only this then doesn't render again as another one streams.
export const useCitedConversation = ({ conversationId, messages }: Conversation): CitedConversation => {
    const sources = useConversationSources(messages);

    return useMemo(() => ({ conversationId, sources }), [conversationId, sources]);
};

// Split from `useMessageContent` so a caller that only renders citation markers does not pay for a
// second pass of `formatCitedContent` over the message body on every streamed chunk.
export const useCitedContentHelpers = ({ sources }: CitedConversation) => {
    const isContentCitedSource = useCallback((children?: string, href?: string): boolean => {
        return isCitationReference(children) || isCitationReference(href);
    }, []);

    const isContentMailto = useCallback((href: string): boolean => mailtoAddress(href) !== undefined, []);

    const getSourceById = useCallback((sourceId: number): ConversationSource | undefined => {
        // source ids are used for citations
        return sources.find(({ citationIndex }) => citationIndex === sourceId);
    }, [sources]);

    const getParsedSource = useCallback((children?: string, href?: string) => {
        const parsedSourceReference = parseCitationIndex(children, href);
        const matchedSource = parsedSourceReference
            ? getSourceById(parsedSourceReference)
            : null;

        return { parsedSourceReference, matchedSource };
    }, [getSourceById]);

    const extractEmail = useCallback((href: string): string => mailtoAddress(href) ?? "", []);

    return {
        isContentCitedSource,
        isContentMailto,
        getSourceById,
        getParsedSource,
        extractEmail
    };
};

export const useMessageContent = (cited: CitedConversation, message: ConversationMessage) => {
    const { content } = message;
    const helpers = useCitedContentHelpers(cited);
    const formattedContent = useMemo((): string => formatCitedContent(content), [content]);

    return { formattedContent, ...helpers };
};
